import { v4 as uuidv4 } from 'uuid';

import type {
  FormColumn,
  FormRow,
  FormSection,
  ReorderDirection,
  WorkflowField,
} from '@/api/workflows';

import type {
  EditFieldPayload,
  FormEditorDataSource,
  InsertCompoundPayload,
  MoveFieldPayload,
  PlaceFieldPayload,
} from '../FormEditorDataSource';

/**
 * Pure, in-memory mirror of the live workflow's granular section/row/column/field
 * mutations — used by the Custom Editor, which has no real `WorkflowTemplate` behind
 * it. Every {@link FormEditorDataSource} method here mutates a local React state tree
 * synchronously (via the `setSections` updater) instead of calling `workflowsApi`; the
 * Custom Editor page persists the whole result to `customElementsApi.updateSection`/
 * `updateStep` only when the originator clicks the page's explicit Save button —
 * matching the locked "explicit Save, no debounce/lock" model for library items.
 */
export function createInMemoryFormEditorDataSource(
  setSections: (updater: (prev: FormSection[]) => FormSection[]) => void,
): FormEditorDataSource {
  return {
    addSection: (payload) => {
      const created: FormSection = {
        id: uuidv4(),
        titleEn: payload.titleEn,
        titleFr: payload.titleFr ?? '',
        rows: [],
        conditionCombinator: null,
        conditions: [],
      };
      setSections((prev) => [...prev, created]);
      return Promise.resolve(created);
    },

    editSectionTitle: (sectionId, titleEn) => {
      setSections((prev) => mapSections(prev, sectionId, (s) => ({ ...s, titleEn })));
      return Promise.resolve();
    },

    deleteSection: (sectionId) => {
      setSections((prev) => prev.filter((s) => s.id !== sectionId));
      return Promise.resolve();
    },

    moveSection: (sectionId, direction) => {
      setSections((prev) => swapAdjacent(prev, sectionId, direction));
      return Promise.resolve();
    },

    addRow: (sectionId, atIndex) => {
      const created: FormRow = { id: uuidv4(), columns: [{ id: uuidv4(), field: null }] };
      setSections((prev) =>
        mapSections(prev, sectionId, (s) => {
          const rows = [...s.rows];
          rows.splice(atIndex ?? rows.length, 0, created);
          return { ...s, rows };
        }),
      );
      return Promise.resolve(created);
    },

    deleteRow: (sectionId, rowId) => {
      setSections((prev) =>
        mapSections(prev, sectionId, (s) => ({ ...s, rows: s.rows.filter((r) => r.id !== rowId) })),
      );
      return Promise.resolve();
    },

    moveRow: (sectionId, rowId, direction) => {
      setSections((prev) =>
        mapSections(prev, sectionId, (s) => ({
          ...s,
          rows: swapAdjacent(s.rows, rowId, direction),
        })),
      );
      return Promise.resolve();
    },

    addColumn: (sectionId, rowId) => {
      setSections((prev) =>
        mapSections(prev, sectionId, (s) =>
          mapRows(s, rowId, (r) => ({
            ...r,
            columns: [...r.columns, { id: uuidv4(), field: null }],
          })),
        ),
      );
      return Promise.resolve();
    },

    removeColumn: (sectionId, rowId, columnId) => {
      setSections((prev) =>
        mapSections(prev, sectionId, (s) =>
          mapRows(s, rowId, (r) => ({ ...r, columns: r.columns.filter((c) => c.id !== columnId) })),
        ),
      );
      return Promise.resolve();
    },

    placeField: (sectionId, rowId, columnId, payload) => {
      const field = buildField(uuidv4(), payload);
      setSections((prev) =>
        mapSections(prev, sectionId, (s) =>
          mapRows(s, rowId, (r) => mapColumns(r, columnId, (c) => ({ ...c, field }))),
        ),
      );
      return Promise.resolve();
    },

    moveField: (payload) => {
      setSections((prev) => moveFieldAcross(prev, payload));
      return Promise.resolve();
    },

    insertCompound: (sectionId, payload) => {
      const newRows = buildCompoundRows(payload);
      setSections((prev) =>
        mapSections(prev, sectionId, (s) => ({ ...s, rows: [...s.rows, ...newRows] })),
      );
      return Promise.resolve();
    },

    editField: (sectionId, rowId, columnId, payload) => {
      setSections((prev) =>
        mapSections(prev, sectionId, (s) =>
          mapRows(s, rowId, (r) =>
            mapColumns(r, columnId, (c) =>
              c.field ? { ...c, field: applyFieldEdit(c.field, payload) } : c,
            ),
          ),
        ),
      );
      return Promise.resolve();
    },

    removeField: (sectionId, rowId, columnId) => {
      setSections((prev) =>
        mapSections(prev, sectionId, (s) =>
          mapRows(s, rowId, (r) => mapColumns(r, columnId, (c) => ({ ...c, field: null }))),
        ),
      );
      return Promise.resolve();
    },

    // Local state is already current the instant each method above resolves —
    // there's nothing to re-fetch.
    refresh: () => Promise.resolve(),
  };
}

function mapSections(
  sections: FormSection[],
  sectionId: string,
  fn: (s: FormSection) => FormSection,
): FormSection[] {
  return sections.map((s) => (s.id === sectionId ? fn(s) : s));
}

function mapRows(section: FormSection, rowId: string, fn: (r: FormRow) => FormRow): FormSection {
  return { ...section, rows: section.rows.map((r) => (r.id === rowId ? fn(r) : r)) };
}

function mapColumns(row: FormRow, columnId: string, fn: (c: FormColumn) => FormColumn): FormRow {
  return { ...row, columns: row.columns.map((c) => (c.id === columnId ? fn(c) : c)) };
}

function swapAdjacent<T extends { id: string }>(
  items: T[],
  id: string,
  direction: ReorderDirection,
): T[] {
  const idx = items.findIndex((i) => i.id === id);
  if (idx === -1) return items;
  const swapWith = direction === 'Up' ? idx - 1 : idx + 1;
  if (swapWith < 0 || swapWith >= items.length) return items;
  const next = [...items];
  const a = next[idx]!;
  const b = next[swapWith]!;
  next[idx] = b;
  next[swapWith] = a;
  return next;
}

function moveFieldAcross(sections: FormSection[], payload: MoveFieldPayload): FormSection[] {
  const sourceSection = sections.find((s) => s.id === payload.fromSectionId);
  const sourceRow = sourceSection?.rows.find((r) => r.id === payload.fromRowId);
  const sourceColumn = sourceRow?.columns.find((c) => c.id === payload.fromColumnId);
  const field = sourceColumn?.field ?? null;
  if (!field) return sections;

  return sections.map((s) => {
    let next = s;
    if (next.id === payload.fromSectionId) {
      next = mapRows(next, payload.fromRowId, (r) =>
        mapColumns(r, payload.fromColumnId, (c) => ({ ...c, field: null })),
      );
    }
    if (next.id === payload.toSectionId) {
      next = mapRows(next, payload.toRowId, (r) =>
        mapColumns(r, payload.toColumnId, (c) => ({ ...c, field })),
      );
    }
    return next;
  });
}

function buildCompoundRows(payload: InsertCompoundPayload): FormRow[] {
  return payload.rows.map((row) => ({
    id: uuidv4(),
    columns: row.columns.map((spec) => ({
      id: uuidv4(),
      field: spec ? buildField(uuidv4(), spec) : null,
    })),
  }));
}

function buildField(id: string, payload: PlaceFieldPayload): WorkflowField {
  // Copy-through: every self-contained property key falls through into the field
  // by default, so a newly-added field property is carried automatically instead
  // of being silently dropped (mirrors `fieldToPayload` in toCustomPayload.ts).
  // Only the identity / top-level wire keys that never live in `properties`, and
  // the two structurally-wider keys (`options`, `repeatableTemplate`) whose stored
  // shape is narrower than the domain shape, are handled explicitly.
  const { options, repeatableTemplate, ...rest } = payload.properties ?? {};
  return {
    ...rest,
    id,
    type: payload.type,
    labelEn: payload.labelEn,
    labelFr: payload.labelFr ?? '',
    required: payload.required ?? false,
    // `labelFr` is optional on the wire but a required string on the domain
    // option; default it. Every other option key spreads through untouched.
    options: (options ?? []).map((o) => ({ ...o, labelFr: o.labelFr ?? '' })),
    repeatableTemplate:
      repeatableTemplate?.map((c) => ({
        ...c,
        labelFr: c.labelFr ?? '',
        options: (c.options ?? []).map((o) => ({ ...o, labelFr: o.labelFr ?? '' })),
      })) ?? null,
    // Custom Field library items are condition-stripped on save, so a freshly
    // placed field starts unconditional; `applyFieldEdit` restores the live
    // field's own conditions on top of this for the edit path.
    conditionCombinator: null,
    conditions: [],
  };
}

function applyFieldEdit(field: WorkflowField, payload: EditFieldPayload): WorkflowField {
  return {
    ...buildField(field.id, { type: field.type, ...payload }),
    conditionCombinator: field.conditionCombinator,
    conditions: field.conditions,
  };
}
